import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { collection, deleteDoc, doc, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { FirebaseError, getApp } from 'firebase/app';
import { User as FBUser, GoogleAuthProvider, OAuthProvider, getAuth, getRedirectResult, sendPasswordResetEmail, signInWithCredential, signInWithCustomToken, signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, signOut, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, updatePassword } from "firebase/auth";
import { AuthenticateViaOfficeWaveResponse } from '../models/DTO';
import { User, UserFCMToken } from '../models/User';
import { FireClient, getFunc } from './FireClient';
import { My } from './My';
import { PushService } from './Utils/PushService';
import { Util } from './Utils/Util';
import { defaultLanguage } from '../locales/InitTranslation';

export type LoginProviderType = 'google' | 'apple';
interface CapUser {
  displayName: string | null;
  email: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  phoneNumber: string | null;
  photoUrl: string | null;
  providerId: string;
  tenantId: string | null;
  uid: string;
}

export class FireClientAuth {
  loggedInUser: FBUser | null = null;
  appUser?: User;

  onLoginStateChanged: (user: User | null) => void = () => {
    console.error('What! onAuthStateChanged is not overridden. Oh no!');
  };

  constructor(private client: FireClient) {
    if (!Capacitor.isNativePlatform()) {
      PushService.onTokenRefreshCallback = async (token) => {
        if (this.appUser) {
          console.log('FireClientAuth: Updating refreshed FCM token');
          await this.registerFCMToken(this.appUser.id);
        }
      };
    }
  }

  getFBAuth() {
    const _auth = getAuth();
    return _auth;
  }

  async loginUser(provider: LoginProviderType): Promise<string> {
    try {
      if (provider == 'google' || provider == 'apple') {
        if (provider == 'apple') {
          let res = await FirebaseAuthentication.signInWithApple();

          let credential = new OAuthProvider("apple.com").credential({
            idToken: res.credential?.idToken,
            accessToken: undefined,
            rawNonce: res.credential?.nonce
          });
          let userCred = await signInWithCredential(this.getFBAuth(), credential);
          if (userCred.user.uid && res.credential?.authorizationCode) {
            const func = getFunc('createRefreshToken');
            func({ userId: userCred.user.uid, authCode: res.credential.authorizationCode });
          }
        } else if (Capacitor.isNativePlatform()) {
          // iOS/Android
          console.log('FireClientAuth: Login as native Platform, iOS or Android', 1111, provider);
          if (provider == 'google') {
            let res = await FirebaseAuthentication.signInWithGoogle({ customParameters: [{ key: 'prompt', value: 'select_account' }] }); //https://github.com/robingenz/capacitor-firebase/blob/main/packages/authentication/README.md#installation
            console.log('signInWithGoogle RESULT:', res);
            console.log('idToken:', res.credential?.idToken);
            console.log('accessToken:', res.credential?.accessToken);

            if (!res.credential?.idToken) {
              console.error('❌ ERROR: idToken is missing from Google Sign-In response!');
              throw new Error('Google Sign-In failed: No ID token received. Please check Firebase configuration and SHA-1 fingerprint.');
            }

            let credential = GoogleAuthProvider.credential(res.credential.idToken);
            console.log('Created credential, calling signInWithCredential...');
            await signInWithCredential(this.getFBAuth(), credential);
            console.log('✅ signInWithCredential successful');
          }
        } else {
          // Web
          const auth = this.getFBAuth();
          if (provider == 'google') {
            let gp = new GoogleAuthProvider();
            gp.setCustomParameters({ prompt: 'select_account' })
            // authDomain 과 앱 오리진이 다르면(예: nextpod-test.web.app ↔ devapp.nextpod.jp)
            // 브라우저의 서드파티 저장소 차단으로 signInWithRedirect 복귀 결과가 유실된다
            // (getRedirectResult 가 비어서 로그인 실패). 이 경우에만 팝업으로 우회 —
            // 오리진이 같은 정규 도메인(devapp/운영)은 기존 redirect 동작을 그대로 유지한다.
            if (auth.config.authDomain && auth.config.authDomain !== window.location.hostname) {
              await signInWithPopup(auth, gp);
            } else {
              // 페이지 전환(redirect) 방식. promise 는 내비게이션과 함께 사라지므로 resolve 되지 않고,
              // 취소 후 뒤로가기 복귀는 LoginPage 의 pageshow(bfcache) 리스너가 로딩 상태를 해제한다 (QA-01).
              // 복귀 결과는 handleOnAuthStateChanged 의 getRedirectResult 가 처리.
              await Promise.race([
                signInWithRedirect(auth, gp),
                // 내비게이션이 시작되지 못한 채 남는 극단 케이스(ESC 중단 등) 대비 — 버튼 잠김 방지
                new Promise<void>(resolve => setTimeout(resolve, 15_000)),
              ]);
            }
          }
          else if (provider == 'apple') {
            alert("NOT SUPPORTED YET")
            await FirebaseAuthentication.signInWithApple();
          }
        }
      }
      else
        throw new Error('Method not implemented.');
    } catch (error) {
      let err = error as Error;
      if (err.message.includes('Duplicate credential received.')) {

      }
      if (FireClientAuth.isUserCancelledError(error)) {
        // 사용자가 팝업/네이티브 시트를 직접 닫은 경우 — 에러 배너를 띄우지 않음
        console.log('Login cancelled by user');
        return "";
      }
      console.error("Login failed", error);
      return err.message;
    }
    return "";
  }

  /** 사용자가 로그인 팝업(웹) 또는 네이티브 로그인 시트를 스스로 닫거나 취소했는지 판별 */
  private static isUserCancelledError(error: unknown): boolean {
    const code = (error as FirebaseError)?.code ?? '';
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request' || code === 'auth/user-cancelled') {
      return true;
    }
    // capacitor-firebase 네이티브 취소: Android "12501"/"canceled the sign-in flow",
    // iOS Apple 로그인 취소: ASAuthorizationError "error 1001"
    const message = (error as Error)?.message ?? '';
    return /canceled|cancelled|12501|error 1001/i.test(message);
  }

  // Run this on startup
  async handleOnAuthStateChanged() {
    let auth = this.getFBAuth();

    auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Supabase에 Firebase JWT 주입 (게시판/캘린더용 RLS 인증)
        try {
          const token = await user.getIdToken();
          const { setSupabaseFirebaseToken } = await import('./SupabaseClient');
          setSupabaseFirebaseToken(token);
        } catch (e) {
          console.warn('[Auth] Supabase token injection failed (non-fatal):', e);
        }

        this.loginHandler(this.fireUserToCapUser(user));

        if (!Util.isLocalDevelopment()) {
          let analytics = getAnalytics(getApp());
          setUserId(analytics, user.email ?? "--");
          logEvent(analytics, 'logged_in');
          setUserProperties(analytics, { platform: Capacitor.getPlatform() });
        } else {
          // console.log('Analytics off');
        }
      }
      else {
        // Supabase 토큰 해제
        try {
          const { setSupabaseFirebaseToken } = await import('./SupabaseClient');
          setSupabaseFirebaseToken(null);
        } catch {
          // Supabase 미설정 환경에서는 무시
        }

        this.loginHandler(null);
        // console.log('onAuthStateChanged: User is not logged in');
      }
    });

    if (!Capacitor.isNativePlatform()) {
      // signInWithRedirect 복귀 시 로그인 결과 처리
      const result = await getRedirectResult(this.getFBAuth());
      if (result) {
        let credential = GoogleAuthProvider.credentialFromResult(result!);
        if (credential) {
          await signInWithCredential(this.getFBAuth(), credential);
        }
      }
    }
  }

  private async loginHandler(capUser: CapUser | null) {
    if (capUser) {
      try {
        // Permission 서비스 초기화 (Tier 정보 로드)
        const { Perm } = await import('./Permission');
        await Perm.initialize();

        // 일반 유저 처리
        this.appUser = await this.client.getUser(capUser.uid);
        if (!this.appUser) {
          // 신규 유저: 브라우저 감지 언어를 기본값으로 설정
          this.appUser = this.capUserToUser(capUser);
          this.appUser.lang = defaultLanguage;
          this.appUser.lastActiveAt = new Date();
          this.onLoginStateChanged(this.appUser);
          await this.client.saveUser(this.appUser);
        } else {
          // 기존 유저: lang 미설정 시 브라우저 감지 언어로 자동 설정
          if (!this.appUser.lang) {
            this.appUser.lang = defaultLanguage;
            this.client.saveUser(this.appUser); // 백그라운드 저장 (await 불필요)
          }
          this.onLoginStateChanged(this.appUser);
          // 하루에 한 번 lastActiveAt 업데이트
          this.updateLastActiveIfNeeded(capUser.uid, this.appUser.lastActiveAt);
        }
      } catch (error) {
        // ⚠️ loginHandler 실패 시에도 반드시 로그인 상태를 전달하여
        // 앱이 로딩 화면에 무한히 머무르지 않도록 방어
        console.error('[Auth] loginHandler failed — forcing state transition:', error);
        // capUser 정보로 최소한의 User 객체 생성하여 전달 시도
        if (!this.appUser) {
          this.appUser = this.capUserToUser(capUser);
        }
        this.onLoginStateChanged(this.appUser);
      }

      // FCM 토큰 발급 및 등록
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        // PWA (Standalone 모드)에서만 푸시 토큰 등록
        import('../pwa-push').then(async module => {
          const token = await module.requestPushPermission();
          if (token) {
            PushService.currentFCMToken = token;
            await this.registerFCMToken(capUser.uid);
          }
        }).catch(err => {
          // 푸시 토큰 등록 실패는 앱 동작에 치명적이지 않음 — 조용히 로깅만
          console.info('[Auth] PWA push 등록 생략/실패:', err);
        });
      } else if (Capacitor.isNativePlatform()) {
        // 네이티브 앱: 로그인 완료 후 푸시 서비스 초기화 (권한 요청 포함)
        // 화면이 완전히 로드된 후 1초 지연하여 권한 다이얼로그 표시

        // 토큰이 이미 있으면 바로 등록
        if (PushService.currentFCMToken) {
          this.registerFCMToken(capUser.uid);
        }

        // 토큰 갱신/등록 콜백 설정 (네이티브용)
        PushService.onTokenRefreshCallback = async (token) => {
          console.log('FireClientAuth: Native FCM token received/refreshed:', token?.substring(0, 20) + '...');
          await this.registerFCMToken(capUser.uid);
        };

        setTimeout(() => {
          PushService.init();
        }, 1000);
      }
      // 일반 웹 브라우저 (non-PWA): 토큰 등록하지 않음 (불필요한 토큰 누적 방지)
    } else {
      this.appUser = undefined;
      this.onLoginStateChanged(null);
    }
  }

  private async updateLastActiveIfNeeded(userId: string, lastActiveAt?: Date | any) {
    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD

      let lastActiveStr = '';
      if (lastActiveAt) {
        // Firestore Timestamp인 경우 toDate() 호출
        let lastDate: Date;
        if (lastActiveAt?.toDate && typeof lastActiveAt.toDate === 'function') {
          lastDate = lastActiveAt.toDate();
        } else if (lastActiveAt instanceof Date) {
          lastDate = lastActiveAt;
        } else {
          lastDate = new Date(lastActiveAt);
        }

        // Invalid Date 체크
        if (!isNaN(lastDate.getTime())) {
          lastActiveStr = lastDate.toISOString().split('T')[0];
        }
      }

      if (todayStr !== lastActiveStr) {
        // 날짜가 다르면 업데이트
        const { updateDoc, doc } = await import('firebase/firestore');
        await updateDoc(doc(this.client.store, User.COL, userId), {
          lastActiveAt: today
        });
        if (this.appUser) {
          this.appUser.lastActiveAt = today;
        }
        console.log('Updated lastActiveAt:', todayStr);
      }
    } catch (error) {
      console.warn('updateLastActiveIfNeeded error (non-fatal):', error);
    }
  }

  setLanguageCode = async () => {
    await FirebaseAuthentication.setLanguageCode({ languageCode: 'en-US' });
  };

  async logoutUser(): Promise<void> {
    const userId = My.user.id;
    const isPWA = !Capacitor.isNativePlatform()
      && window.matchMedia?.('(display-mode: standalone)').matches;

    if (userId) {
      // 저장된 문서 ID로 삭제 (PWA: deviceId, 네이티브: FCM 토큰)
      const storedDocId = localStorage.getItem(FireClientAuth.FCM_TOKEN_STORAGE_KEY);
      if (storedDocId) {
        try {
          await deleteDoc(doc(this.client.store, User.COL, userId, UserFCMToken.COL, storedDocId));
        } catch (error) {
          console.warn('logoutUser: Failed to delete token doc (non-fatal):', error);
        }
      }

      // Fallback: 현재 FCM 토큰으로도 삭제 시도 (레거시 문서 정리)
      if (PushService.currentFCMToken && PushService.currentFCMToken !== storedDocId) {
        try {
          await deleteDoc(doc(this.client.store, User.COL, userId, UserFCMToken.COL, PushService.currentFCMToken));
        } catch {
          // 레거시 문서가 없어도 무시
        }
      }
    }

    localStorage.removeItem(FireClientAuth.FCM_TOKEN_STORAGE_KEY);
    // PWA deviceId는 브라우저에 유지 (다음 로그인 시 같은 문서 ID 재사용)
    await signOut(this.getFBAuth());
  }


  async doesAccountExist(loginEmail: string): Promise<boolean> {
    const info = await this.checkAccountInfo(loginEmail);
    return info.exists;
  }

  async checkAccountInfo(loginEmail: string): Promise<{ exists: boolean; hasPassword: boolean; hasGoogle: boolean; hasApple: boolean }> {
    const func = getFunc('doesAccountExist');
    const result = await func({ email: loginEmail });
    return result.data as { exists: boolean; hasPassword: boolean; hasGoogle: boolean; hasApple: boolean };
  }

  async registerNewUser(loginEmail: string, loginName: string, loginPassword: string, loginImage: string) {
    const func = getFunc('createNewUser');
    const result = await func({ email: loginEmail, name: loginName, password: loginPassword, photoURL: loginImage });
    let token = result.data as string; // token
    // let signin = await FirebaseAuthentication.signInWithCustomToken({ token: token });
    // let signin = await signInWithCustomToken(this.getFBAuth(), token);
    // await this.loginHandler(this.fireUserToCapUser(signin.user));
    if (token !== 'FAILED') {
      await this.signinWithLoginToken(token);
    } else {
      throw new Error("Oops! The server could not create a user.")
    }
  }

  async signinWithLoginToken(token: string) {
    let signin = await signInWithCustomToken(this.getFBAuth(), token);
    await this.loginHandler(this.fireUserToCapUser(signin.user));
  }

  async loginUserWithPassword(loginEmail: string, loginPassword: string): Promise<'success' | 'wrong-pass' | 'too-many-tries' | 'no-password' | 'error'> {
    try {
      let res = await signInWithEmailAndPassword(this.getFBAuth(), loginEmail, loginPassword);
      let capuser = this.fireUserToCapUser(res.user);
      await this.loginHandler(capuser);
      return 'success';
    } catch (error) {
      let er = error as FirebaseError;
      console.log('loginUserWithPassword error', er.message, er.code);
      // Firebase SDK v10+: wrong-password가 auth/invalid-credential로 통합됨
      if (er.message.includes('wrong-password') || er.message.includes('INVALID_LOGIN_CREDENTIALS') || er.code === 'auth/invalid-credential')
        return 'wrong-pass';
      else if (er.message.includes('too-many-requests'))
        return 'too-many-tries';
      else
        return 'error';
    }
  }

  async deleteAccount() {
    try {
      const func = getFunc('deleteUserAccount');
      await func();
    } catch (error) {
      alert("Failed to delete user account. Please contact support.");
    }
    await this.logoutUser();
  }

  async sendResetPasswordLink(email: string) {
    await sendPasswordResetEmail(this.getFBAuth(), email);
  }

  /**
   * 신규 회원가입용 이메일 인증 링크 발송
   */
  async sendEmailVerificationLink(email: string) {
    const actionCodeSettings = {
      // 인증 후 돌아올 URL
      url: window.location.origin + '/login?emailSignUp=true',
      handleCodeInApp: true,
    };

    await sendSignInLinkToEmail(this.getFBAuth(), email, actionCodeSettings);
    // 링크 클릭 시 사용하기 위해 이메일을 로컬 스토리지에 저장
    window.localStorage.setItem('emailForSignUp', email);
  }

  /**
   * 현재 URL이 이메일 인증 링크인지 확인
   */
  isEmailSignInLink(): boolean {
    try {
      return isSignInWithEmailLink(this.getFBAuth(), window.location.href);
    } catch (e) {
      console.warn('isEmailSignInLink check failed:', e);
      return false;
    }
  }

  /**
   * 이메일 링크로 회원가입 완료
   */
  async completeEmailSignUp(email: string, password: string, name: string, photoURL: string) {
    // 1. 이메일 링크로 임시 로그인
    const userCredential = await signInWithEmailLink(this.getFBAuth(), email, window.location.href);

    // 2. 비밀번호 설정
    if (userCredential.user) {
      await updatePassword(userCredential.user, password);
    }

    // 3. 서버에서 사용자 프로필 생성
    const func = getFunc('createNewUser');
    await func({ email, name, password, photoURL, skipAuth: true });

    // 4. 로컬 스토리지 정리
    window.localStorage.removeItem('emailForSignUp');

    // 5. 로그인 처리
    const capuser = this.fireUserToCapUser(userCredential.user);
    await this.loginHandler(capuser);
  }

  /**
   * 로컬 스토리지에서 저장된 이메일 가져오기
   */
  getStoredEmailForSignUp(): string | null {
    return window.localStorage.getItem('emailForSignUp');
  }

  // async whoAmI(uid: string) {
  //   console.log('----------------- whoAmI ------------------');
  //   const functions = getFunctions();
  //   const func = httpsCallable(functions, 'whoAmI');
  //   const result = await func({});
  //   let token = result.data as string;
  //   console.log('RECEIVED: ', result.data);
  //   console.log('MY ID IS: ', uid);
  // }

  private capUserToUser(fu: CapUser): User {
    let user = new User(fu.uid, fu.displayName ?? fu.email?.split('@')[0] ?? "Name", fu.email ?? "", fu.photoUrl, "");
    return user;
  }

  private fireUserToCapUser(user: FBUser): CapUser {
    return { displayName: user.displayName, email: user.email, uid: user.uid } as CapUser;
  }

  private static readonly FCM_TOKEN_STORAGE_KEY = 'fcm-registered-token';
  private static readonly DEVICE_ID_KEY = 'pwa-device-id';

  /**
   * PWA 브라우저 고유 Device ID를 가져오거나 새로 생성.
   * 같은 브라우저에서는 항상 동일한 ID가 반환되어, Firestore 문서가 덮어써짐.
   * 다른 브라우저에서는 새 ID가 생성되어 별도 문서로 관리됨.
   */
  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem(FireClientAuth.DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = `pwa_${crypto.randomUUID()}`;
      localStorage.setItem(FireClientAuth.DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  }

  private async registerFCMToken(userId: string) {
    if (PushService.currentFCMToken) {
      try {
        const newToken = PushService.currentFCMToken;
        const platform = (Capacitor.getPlatform() || 'web') as 'android' | 'ios' | 'web';
        const isPWA = !Capacitor.isNativePlatform()
          && window.matchMedia?.('(display-mode: standalone)').matches;

        // PWA: deviceId를 문서 ID로 사용 → 같은 브라우저에서 항상 같은 문서 덮어쓰기
        // 네이티브: 기존대로 FCM 토큰을 문서 ID로 사용
        const docId = isPWA ? this.getOrCreateDeviceId() : newToken;
        const prevDocId = localStorage.getItem(FireClientAuth.FCM_TOKEN_STORAGE_KEY);

        // 네이티브: 이전 토큰과 다르면 stale 토큰 문서 삭제
        if (!isPWA && prevDocId && prevDocId !== newToken) {
          try {
            await deleteDoc(doc(this.client.store, User.COL, userId, UserFCMToken.COL, prevDocId));
          } catch {
            // 이전 문서가 이미 없어도 무시
          }
        }

        // 토큰 등록 (최소 필드만 저장)
        const tokenData = {
          token: newToken,
          userId,
          platform,
          updated: serverTimestamp()
        };
        await setDoc(doc(this.client.store, User.COL, userId, UserFCMToken.COL, docId), tokenData);

        // localStorage에 현재 문서 ID 기록
        localStorage.setItem(FireClientAuth.FCM_TOKEN_STORAGE_KEY, docId);

        // PWA: 현재 deviceId 문서 외의 레거시 web 토큰 문서를 일괄 정리
        if (isPWA) {
          this.cleanupLegacyWebTokens(userId, docId).catch(() => {});
        }
      } catch (error) {
        console.warn('registerFCMToken error (non-fatal):', error);
      }
    }
  }

  /**
   * PWA 환경에서 현재 deviceId 문서 외의 web 플랫폼 토큰 문서를 정리.
   * 레거시 FCM-토큰-as-문서-ID 시절의 잔여 문서를 삭제한다.
   */
  private async cleanupLegacyWebTokens(userId: string, currentDocId: string) {
    const colRef = collection(this.client.store, User.COL, userId, UserFCMToken.COL);
    const snap = await getDocs(colRef);
    for (const d of snap.docs) {
      const data = d.data();
      // web 플랫폼이면서 현재 문서가 아닌 것만 삭제
      if (d.id !== currentDocId && (!data.platform || data.platform === 'web')) {
        try {
          await deleteDoc(d.ref);
        } catch {
          // 무시
        }
      }
    }
  }

  async signinWithOfficeWaveToken(officeWaveToken: string | null, test: string | null) {
    const func = getFunc('authenticateViaOfficeWave');
    const r = await func({ token: officeWaveToken, test: test });
    const data = r.data as AuthenticateViaOfficeWaveResponse;
    if (data.loginToken) {
      await this.signinWithLoginToken(data.loginToken);
    } else {
      throw new Error(data.error ?? "Oops! The server could not create a user.")
    }
  }
}

// firebase.auth()
//   .getRedirectResult()
//   .then(async (result) => {
//     console.log("getRedirectResult");
//     if (result.credential) {
//       /** @type {firebase.auth.OAuthCredential} */
//     }
//     if (result.user) {
//       const existing = await this.store.collection('users').doc(result.user.uid).get();
//       if (!existing.exists) {
//         const appUser = Util.fireUserToUser(result.user)
//         await this.store.collection('users').doc(appUser?.id).set(Util.toPOJO(appUser));
//         handler(Util.toPOJO(appUser));
//         console.log("Stored a new user")
//       }
//     }
//   }).catch((error) => {
//     // var errorCode = error.code;
//     // var errorMessage = error.message;
//     // var email = error.email;
//     // var credential = error.credential;
//     console.log("getRedirectResult threw error.");
//     console.error(error);
//   });
//     // var errorMessage = error.message;
//     // var email = error.email;
//     // var credential = error.credential;
//     console.log("getRedirectResult threw error.");
//     console.error(error);
//   });
